import { createServerFn } from "@tanstack/react-start";
import { eq, desc } from "drizzle-orm";
import { z } from "zod";
import { mkdir, writeFile, unlink } from "node:fs/promises";
import { join, extname } from "node:path";
import { randomUUID } from "node:crypto";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { getServerConfig } from "../config.server";
import { getMediaUsage } from "../media/resolve.server";

const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/svg+xml"]);
const MAX_SIZE = 5 * 1024 * 1024;

export const listMedia = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("media", "read");
  return getDb().select().from(schema.mediaFiles).orderBy(desc(schema.mediaFiles.createdAt));
});

export const getMediaById = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("media", "read");
    const [row] = await getDb()
      .select()
      .from(schema.mediaFiles)
      .where(eq(schema.mediaFiles.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const getMediaUsageInfo = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("media", "read");
    return getMediaUsage(data.id);
  });

export const uploadMedia = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      filename: z.string(),
      mimeType: z.string(),
      dataBase64: z.string(),
      altText: z.string().optional(),
    }),
  )
  .handler(async ({ data }) => {
    const user = await requirePermission("media", "write");
    if (!ALLOWED_TYPES.has(data.mimeType)) throw new Error("File type not allowed");
    const buffer = Buffer.from(data.dataBase64, "base64");
    if (buffer.length > MAX_SIZE) throw new Error("File too large (max 5MB)");

    const { uploadDir } = getServerConfig();
    await mkdir(uploadDir, { recursive: true });
    const ext = extname(data.filename) || ".bin";
    const storedName = `${randomUUID()}${ext}`;
    const diskPath = join(uploadDir, storedName);
    await writeFile(diskPath, buffer);
    const publicPath = `/uploads/${storedName}`;

    const [row] = await getDb()
      .insert(schema.mediaFiles)
      .values({
        filename: data.filename,
        path: publicPath,
        mimeType: data.mimeType,
        size: buffer.length,
        altText: data.altText,
        uploadedBy: user.id,
      })
      .returning();
    return row;
  });

export const deleteMedia = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid(), force: z.boolean().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("media", "delete");
    const usages = await getMediaUsage(data.id);
    if (usages.length > 0 && !data.force) {
      throw new Error(`Media is used by ${usages.length} item(s). Remove references first or force delete.`);
    }
    const db = getDb();
    const [file] = await db.select().from(schema.mediaFiles).where(eq(schema.mediaFiles.id, data.id)).limit(1);
    if (file) {
      const { uploadDir } = getServerConfig();
      const filename = file.path.replace("/uploads/", "");
      try {
        await unlink(join(uploadDir, filename));
      } catch {
        /* file may not exist */
      }
      await db.delete(schema.mediaFiles).where(eq(schema.mediaFiles.id, data.id));
    }
    return { ok: true };
  });
